import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, Globe, Wrench, Users } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import { ServiceGrid } from '../components/ServiceCard';
import { useServices } from '../hooks/useServices';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnimatedStat({ end, suffix, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const duration = 1500;
        const step = (timestamp) => {
          if (!start) start = timestamp;
          const progress = Math.min((timestamp - start) / duration, 1);
          setCount(Math.floor(progress * end));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return (
    <div ref={ref} className="text-center">
      <div className="stat-counter">{count.toLocaleString('de-DE')}{suffix}</div>
      <p className="text-slate-600 text-sm mt-2 font-medium">{label}</p>
    </div>
  );
}

export default function HomePage() {
  const { t, language, lp } = useLanguage();
  const services = useServices();
  const [faqs, setFaqs] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/faqs`).then(r => setFaqs(r.data)).catch(() => {});
    axios.get(`${API}/partners`).then(r => setPartners(r.data)).catch(() => {});
  }, []);

  const getLocalized = (item, field) => item[`${field}_${language}`] || item[`${field}_de`] || '';

  return (
    <div data-testid="home-page">
      <SEO page="home" />
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="/api/uploads/hero_main.jpg" alt={t('hero.imageAlt')} width="1200" height="800" fetchPriority="high" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 animate-fadeInUp" style={{animationDelay: '0.2s', opacity: 0, animationFillMode: 'forwards'}} data-testid="hero-title">
              {t('hero.title')}
            </h1>
            <p className="text-lg text-white/80 mb-10 max-w-2xl leading-relaxed animate-fadeInUp" style={{animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards'}}>
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeInUp" style={{animationDelay: '0.4s', opacity: 0, animationFillMode: 'forwards'}}>
              <Link to={lp('/planner')}>
                <Button className="bg-[#066ce7] hover:bg-[#005bb5] text-white px-8 py-3 text-base font-semibold shadow-xl hover:shadow-2xl transition-all" data-testid="hero-cta-planner">
                  {t('hero.cta1')} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to={lp('/contact')}>
                <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-3 text-base font-semibold backdrop-blur-sm" data-testid="hero-cta-contact">
                  {t('hero.cta2')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-slate-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedStat end={75} suffix="+" label={t('stats.projects')} />
            <AnimatedStat end={3} suffix="+" label={t('stats.years')} />
            <AnimatedStat end={4} suffix="" label={t('stats.countries')} />
            <AnimatedStat end={40000} suffix="+" label={t('stats.sqm')} />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-32 blueprint-bg" data-testid="services-section">
        <div className="max-w-[1480px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#066ce7] text-sm font-semibold uppercase tracking-wider mb-3">{t('services.tag')}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">{t('services.title')}</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">{t('services.subtitle')}</p>
          </div>
          <ServiceGrid services={services} />
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-[#066ce7] text-sm font-semibold uppercase tracking-wider mb-3">{t('about.tag')}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6">{t('about.title')}</h2>
              <p className="text-slate-600 leading-relaxed mb-4">{t('about.text1')}</p>
              <p className="text-slate-600 leading-relaxed mb-8">{t('about.text2')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                {[
                  { icon: Wrench, title: t('about.value1.title'), text: t('about.value1.text') },
                  { icon: Globe, title: t('about.value2.title'), text: t('about.value2.text') },
                  { icon: Users, title: t('about.value3.title'), text: t('about.value3.text') },
                ].map((v, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <v.icon className="w-6 h-6 text-[#066ce7]" />
                    <h4 className="font-bold text-sm text-[#0F172A]">{v.title}</h4>
                    <p className="text-slate-500 text-xs leading-relaxed">{v.text}</p>
                  </div>
                ))}
              </div>
              <Link to={lp('/about')}>
                <Button className="bg-[#066ce7] hover:bg-[#005bb5] text-white px-6" data-testid="about-cta">
                  {t('about.cta')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img src="/api/uploads/team_montage2.jpg" alt={t('about.imageAlt')} width="1200" height="800" loading="lazy" className="w-full h-[450px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#066ce7] text-white p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-extrabold">3+</div>
                <div className="text-sm text-blue-100">{t('stats.years')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      {partners.length > 0 && (
        <section className="py-16 bg-slate-50 border-y border-slate-100" data-testid="partners-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h3 className="text-center text-sm font-semibold uppercase tracking-wider text-slate-400 mb-8">{t('trust.title')}</h3>
            <div className="flex flex-wrap justify-center items-center gap-12">
              {partners.map((p) => {
                const inner = p.logo
                  ? <img src={p.logo} alt={p.name} loading="lazy" className="max-h-14 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300" />
                  : <span className="text-slate-600 text-sm font-semibold">{p.name}</span>;
                return p.website ? (
                  <a key={p.id} href={p.website} target="_blank" rel="noopener noreferrer" className="block" data-testid={`partner-${p.id}`} title={p.name}>{inner}</a>
                ) : (
                  <div key={p.id} className="block" data-testid={`partner-${p.id}`} title={p.name}>{inner}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#066ce7] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '30px 30px'}} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">{t('cta.title')}</h2>
          <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto">{t('cta.subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={lp('/planner')}>
              <Button className="bg-white text-[#066ce7] hover:bg-blue-50 px-8 py-3 text-base font-semibold shadow-xl" data-testid="cta-planner-btn">
                {t('cta.btn1')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to={lp('/contact')}>
              <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-3 text-base font-semibold" data-testid="cta-contact-btn">
                {t('cta.btn2')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-20 md:py-32 bg-white" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-[#066ce7] text-sm font-semibold uppercase tracking-wider mb-3">{t('faq.tag')}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">{t('faq.title')}</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.slice(0, 6).map((faq, i) => (
                <AccordionItem key={faq.id || i} value={`faq-${i}`} className="border border-slate-200 rounded-lg px-6 data-[state=open]:border-[#066ce7] transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-[#0F172A] hover:text-[#066ce7] py-5" data-testid={`faq-trigger-${i}`}>
                    {getLocalized(faq, 'question')}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                    {getLocalized(faq, 'answer')}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}
    </div>
  );
}
